import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import { logout, getCurrentUser } from '../store/slices/authSlice';

// Global cache to prevent multiple concurrent auth checks
let globalLastChecked = 0;
let globalAuthInProgress = false;
let globalAuthPromise: Promise<any> | null = null;
const MIN_AUTH_INTERVAL = 30000; // 30 seconds (reduced from 1 minute)

/**
 * Custom hook for handling authentication
 * 
 * Provides authentication status, user data, and methods for auth management
 * Also ensures auth state is refreshed when the component mounts
 */
export const useAuth = () => {
  console.log('[useAuth] 🎣 useAuth hook called');
  
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, loading, error } = useSelector((state: RootState) => state.auth);
  const [lastChecked, setLastChecked] = useState<number>(globalLastChecked);
  const [authRetryCount, setAuthRetryCount] = useState<number>(0);
  const [isLocallyVerified, setIsLocallyVerified] = useState<boolean>(false);
  
  console.log('[useAuth] 📊 Auth state:', { 
    isAuthenticated, 
    loading, 
    hasUser: !!user, 
    userId: user?.id?.substring(0, 8),
    authRetryCount 
  });
  
  // Use a ref to track if auth check is in progress
  const authCheckInProgress = useRef(globalAuthInProgress);
  
  // Check authentication on mount and set last checked time
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Only check authentication if:
        // 1. There's a token
        // 2. Haven't exceeded retry limit
        // 3. Haven't checked recently (globally)
        // 4. No auth check is already in progress
        const token = localStorage.getItem('authToken');
        const now = Date.now();
        const timeSinceLastCheck = now - globalLastChecked;
        const shouldRefresh = timeSinceLastCheck > MIN_AUTH_INTERVAL; 
        
        if (token && authRetryCount < 3 && shouldRefresh && !globalAuthInProgress) {
          console.log(`[AUTH] Checking authentication status. Retry count: ${authRetryCount}`);
          
          // Set global flag to prevent concurrent checks
          globalAuthInProgress = true;
          authCheckInProgress.current = true;
          
          // Create or reuse global auth promise
          if (!globalAuthPromise) {
            globalAuthPromise = dispatch(getCurrentUser());
          }
          
          try {
            await globalAuthPromise;
            // Update global timestamp
            globalLastChecked = now;
            setLastChecked(now);
            // Reset retry count on success
            setAuthRetryCount(0);
            setIsLocallyVerified(true);
          } catch (authError) {
            console.error('Authentication check failed:', authError);
            // Increment retry count on failure
            setAuthRetryCount(prev => prev + 1);
            // Still update lastChecked to prevent rapid retries
            globalLastChecked = now;
            setLastChecked(now);
            
            if (authRetryCount >= 2) {
              console.error('Authentication failed after 3 attempts. Logging out.');
              handleLogout();
            }
          } finally {
            // Clear flag and promise when finished
            globalAuthInProgress = false;
            authCheckInProgress.current = false;
            globalAuthPromise = null;
          }
        } else if (globalAuthInProgress && globalAuthPromise) {
          // If auth is in progress, wait for the existing promise
          try {
            console.log('[AUTH] Waiting for existing auth check to complete...');
            await globalAuthPromise;
            setIsLocallyVerified(true);
          } catch (error) {
            console.error('Existing auth check failed:', error);
          }
        } else if (token && !shouldRefresh && isAuthenticated) {
          // If we already checked recently and are authenticated, trust the local state
          setIsLocallyVerified(true);
        }
      } catch (error) {
        console.error('Error in authentication check:', error);
        globalAuthInProgress = false;
        authCheckInProgress.current = false;
      }
    };
    
    // Only run checkAuth if not already in progress
    if (!authCheckInProgress.current) {
      checkAuth();
    }
  }, [dispatch, authRetryCount, isAuthenticated]);
  
  // Provide a logout function
  const handleLogout = useCallback(() => {
    dispatch(logout());
    // Reset auth state
    setAuthRetryCount(0);
    setIsLocallyVerified(false);
    setLastChecked(0);
    globalLastChecked = 0;
    globalAuthInProgress = false;
    globalAuthPromise = null;
    localStorage.removeItem('authToken');
  }, [dispatch]);
  
  // Provide a refresh function to manually refresh auth
  const refreshAuth = useCallback(async () => {
    try {
      // Only refresh if:
      // 1. There's a token
      // 2. Haven't exceeded retry limit
      // 3. Haven't checked recently
      // 4. No auth check is already in progress
      const token = localStorage.getItem('authToken');
      const now = Date.now();
      const timeSinceLastCheck = now - globalLastChecked;
      const shouldRefresh = timeSinceLastCheck > MIN_AUTH_INTERVAL;
      
      if (!token || authRetryCount >= 3) {
        return false;
      }
      
      if (!shouldRefresh && isAuthenticated) {
        console.log('[AUTH] Using cached authentication state (global timestamp)');
        setIsLocallyVerified(true);
        return true;
      }
      
      // If another auth check is in progress, wait for it
      if (globalAuthInProgress && globalAuthPromise) {
        console.log('[AUTH] Authentication check already in progress, waiting...');
        try {
          await globalAuthPromise;
          setIsLocallyVerified(true);
          return true;
        } catch (error) {
          console.error('Existing auth check failed:', error);
          return false;
        }
      }
      
      console.log(`[AUTH] Refreshing authentication. Retry count: ${authRetryCount}`);
      
      // Set flag to prevent concurrent checks
      globalAuthInProgress = true;
      authCheckInProgress.current = true;
      
      // Create global auth promise
      globalAuthPromise = dispatch(getCurrentUser());
      
      try {
        await globalAuthPromise;
        // Update global timestamp
        globalLastChecked = now;
        setLastChecked(now);
        // Reset retry count on success
        setAuthRetryCount(0);
        setIsLocallyVerified(true);
        return true;
      } catch (error) {
        console.error('Authentication refresh failed:', error);
        // Increment retry count on failure
        setAuthRetryCount(prev => prev + 1);
        
        if (authRetryCount >= 2) {
          console.error('Authentication failed after 3 attempts. Logging out.');
          handleLogout();
        }
        return false;
      } finally {
        // Clear flag and promise when finished
        globalAuthInProgress = false;
        authCheckInProgress.current = false;
        globalAuthPromise = null;
      }
    } catch (error) {
      console.error('Error in refreshAuth:', error);
      globalAuthInProgress = false;
      authCheckInProgress.current = false;
      return false;
    }
  }, [dispatch, authRetryCount, handleLogout, isAuthenticated]);
  
  return {
    user,
    isAuthenticated: isAuthenticated || isLocallyVerified,
    loading,
    error,
    lastChecked,
    authRetryCount,
    logout: handleLogout,
    refreshAuth
  };
};

export default useAuth; 